const DATESTRING_KEYS = ["createdAt", "updatedAt", "customerNoteUpdatedAt"]

/** `expiring_soon` → `expiringSoon`.
 *
 * THE CHARACTER CLASS STOPS AT LETTERS, AND MUST. This runs over every object in
 * every response, including maps whose keys are CONSTANTS or DATA rather than field
 * names. Widening it to `[a-z0-9]` to camelCase a field like `accepted_last_30d`
 * also rewrites the feature flag `QMS_4_1` to `QMS41` — and the flag then reads
 * false for every user, silently.
 *
 * A backend field that wants camelCase must simply not put an underscore before a
 * digit: `accepted_last30d` converts to `acceptedLast30d`, `accepted_last_30d` does
 * not. That is a rename on one schema, against a change that reaches every payload.
 */
function fromSnakeCaseToCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

function fromCamelCaseToSnakeCase(str: string): string {
  return str.replace(/([A-Z])/g, (match) => `_${match.toLowerCase()}`)
}

export function convertKeysToCamelCase<T>(data: T): T {
  if (Array.isArray(data)) {
    return data.map((item) => convertKeysToCamelCase(item)) as unknown as T
  } else if (data !== null && typeof data === "object") {
    return Object.keys(data).reduce((acc, key) => {
      const camelKey = fromSnakeCaseToCamelCase(key)
      // Type assertion to ensure type safety
      ;(acc as Record<string, unknown>)[camelKey] = convertKeysToCamelCase(
        (data as Record<string, unknown>)[key],
      )
      return acc
    }, {} as T)
  }
  return data
}

export function convertKeysToSnakeCase<T>(data: T): T {
  if (Array.isArray(data)) {
    return data.map((item) => convertKeysToSnakeCase(item)) as unknown as T
  } else if (data !== null && typeof data === "object") {
    return Object.keys(data).reduce((acc, key) => {
      const snakeKey = fromCamelCaseToSnakeCase(key)
      // Type assertion to ensure type safety
      ;(acc as Record<string, unknown>)[snakeKey] = convertKeysToSnakeCase(
        (data as Record<string, unknown>)[key],
      )
      return acc
    }, {} as T)
  }
  return data
}

export function convertDateStringsToDates<T>(data: T): T {
  if (Array.isArray(data)) {
    return data.map((item) => convertDateStringsToDates(item)) as unknown as T
  } else if (data !== null && typeof data === "object") {
    return Object.keys(data).reduce((acc, key) => {
      const value = (data as Record<string, unknown>)[key]
      if (
        DATESTRING_KEYS.includes(key) &&
        typeof value === "string" &&
        !isNaN(Date.parse(value))
      ) {
        // Convert specific date strings to Date objects
        ;(acc as Record<string, unknown>)[key] = new Date(value)
      } else {
        ;(acc as Record<string, unknown>)[key] =
          convertDateStringsToDates(value)
      }
      return acc
    }, {} as T)
  }
  return data
}

export function convertDatesToDateStrings<T>(data: T): T {
  if (Array.isArray(data)) {
    return data.map((item) => convertDatesToDateStrings(item)) as unknown as T
  } else if (data !== null && typeof data === "object") {
    return Object.keys(data).reduce((acc, key) => {
      const value = (data as Record<string, unknown>)[key]
      if (value instanceof Date) {
        // Convert Date objects to ISO date strings directly using toISOString()
        ;(acc as Record<string, unknown>)[key] = value.toISOString()
      } else {
        // Recursively process nested objects and arrays
        ;(acc as Record<string, unknown>)[key] =
          convertDatesToDateStrings(value)
      }
      return acc
    }, {} as T)
  }
  return data
}
